/**
 * Sun azimuth and elevation for an observer, from the Astronomical Almanac's
 * low-precision solar coordinates (the same sun as sunPositionEci in the
 * loader analysis; about 0.01 degree in declination between 1950 and 2050)
 * and the low-precision Greenwich mean sidereal time. Geometric: there is no
 * refraction correction, so at sunrise this reports 0 while the apparent sun
 * already stands about half a degree up.
 */

const DEG = Math.PI / 180;

export interface SolarPosition {
  /** Degrees clockwise from true north, 0 to 360. */
  azimuthDeg: number;
  /** Degrees above the horizon; negative below it. */
  elevationDeg: number;
  declinationDeg: number;
  rightAscensionDeg: number;
  /** Local hour angle in -180..180: 0 on the meridian, positive after it. */
  hourAngleDeg: number;
}

function norm360(deg: number): number {
  return ((deg % 360) + 360) % 360;
}

function wrap180(deg: number): number {
  const d = norm360(deg);
  return d > 180 ? d - 360 : d;
}

/** Days since J2000.0 (2000-01-01 12:00 TT, taken as UTC at this precision). */
export function daysSinceJ2000(timeMs: number): number {
  return timeMs / 86_400_000 + 2_440_587.5 - 2_451_545.0;
}

/** Greenwich mean sidereal time in degrees (0 to 360). */
export function gmstDeg(timeMs: number): number {
  return norm360(280.46061837 + 360.98564736629 * daysSinceJ2000(timeMs));
}

export function solarPosition(latDeg: number, lonDeg: number, timeMs: number): SolarPosition {
  if (!Number.isFinite(latDeg) || !Number.isFinite(lonDeg) || !Number.isFinite(timeMs)) {
    throw new Error(`solarPosition: non-finite input (lat ${latDeg}, lon ${lonDeg}, time ${timeMs})`);
  }
  if (latDeg < -90 || latDeg > 90) throw new Error(`solarPosition: latitude ${latDeg} is outside -90..90`);
  const n = daysSinceJ2000(timeMs);
  // Mean longitude, mean anomaly, ecliptic longitude and obliquity of the ecliptic.
  const meanLon = norm360(280.46 + 0.9856474 * n);
  const g = norm360(357.528 + 0.9856003 * n) * DEG;
  const lambda = (meanLon + 1.915 * Math.sin(g) + 0.02 * Math.sin(2 * g)) * DEG;
  const eps = (23.439 - 0.0000004 * n) * DEG;
  const ra = Math.atan2(Math.cos(eps) * Math.sin(lambda), Math.cos(lambda));
  const dec = Math.asin(Math.sin(eps) * Math.sin(lambda));

  const hourAngleDeg = wrap180(gmstDeg(timeMs) + lonDeg - ra / DEG);
  const h = hourAngleDeg * DEG;
  const lat = latDeg * DEG;
  const sinEl = Math.sin(lat) * Math.sin(dec) + Math.cos(lat) * Math.cos(dec) * Math.cos(h);
  const elevation = Math.asin(Math.max(-1, Math.min(1, sinEl)));
  // Azimuth from north towards east: atan2(east, north) of the sun's horizontal direction.
  const east = -Math.cos(dec) * Math.sin(h);
  const north = Math.sin(dec) * Math.cos(lat) - Math.cos(dec) * Math.sin(lat) * Math.cos(h);
  return {
    azimuthDeg: norm360(Math.atan2(east, north) / DEG),
    elevationDeg: elevation / DEG,
    declinationDeg: dec / DEG,
    rightAscensionDeg: norm360(ra / DEG),
    hourAngleDeg,
  };
}
